import { Component, effect, ElementRef, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { PreviousPageService } from '@shared/services/previous-page.service';
import { HygraphService } from '@shared/services/hygraph/hygraph.service';
import { AssetDetails, HTMLReport } from '@shared/services/hygraph/hygraph.models';
import { ActivatedRoute, Router } from '@angular/router';
import { MetaService } from '@app/@shared/services/meta.service';
import { TelemetryEvent } from '@app/@shared/models/telemetry-event';
import { TelemetryEventName } from '@app/@shared/enums';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { OptionGroup, SocialOption } from '../../generic-toolbar/option-model';
import { StatelessBehavior, ToggleBehavior } from '../../generic-toolbar/state-behavior-model';
import { Icon } from '../../generic-toolbar/icons';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { catchError, concatMap, map } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { selectUserProfile } from '@app/store/Selectors/workspace/workspace.selectors';
import { NavigationService } from '@app/@shared/services/navigation/navigation.service';
import { RegularExpressions } from '@shared/expresions/regular-expressions';
import { ShareItemModalComponent } from '../../share-item-modal/share-item-modal.component';
import { DialogService } from '@app/@shared/services/dialog/dialog.service';
import {
  UserViewHistoryItemSource,
  UserViewHistoryItemType
} from "@shared/models/user-view-history/user-view-history-items";
import { RecentViewHistoryService } from "@shared/services/recent-view-history/recent-view-history.service";
import { waitForIframeContent } from '@app/@shared/utils/common';
import { MyLibraryService } from '@app/@shared/services/my-library/my-library.service';
import { FeatureFlagService, ffKeys } from '@app/@shared/services/featureflag.service';
import { isTablet } from '@app/group/helpers/is-tablet';
import { AuthenticationService } from '@app/auth';
import { isUserAllowedToPreviewHygraph } from '@app/@shared/utils/hygraph-preview-role-selector';

import { HYGRAPH_CONTENT_STAGES } from '@app/@shared/consts/hygraph-preview';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { ShellView } from '@app/shell/shell.model';
import { NotificationSettingsService } from '@app/@shared/services/notification-settings/notification-settings.service';

@UntilDestroy()
@Component({
  selector: 'app-hygraph-viewer',
  templateUrl: './hygraph-viewer.component.html',
  styleUrls: ['./hygraph-viewer.component.scss'],
  animations: [
    trigger('fadeTeaser', [
      state('open', style({
        position: "sticky",
        bottom: 0
      })),
      state('closed', style({
        position: "fixed",
        bottom: "unset"
      })),
      transition('open => closed', [animate(300)]),
      transition('closed => open', [animate(300)]),
    ]),
  ],
  standalone: false
})
export class HygraphViewerComponent implements ShellView, OnInit, OnDestroy {
  @ViewChild('hygraph', { static: false, read: ElementRef }) hygraphElement: ElementRef;
  @ViewChild('invisibleIframe', { static: false }) invisibleIframe: ElementRef;

  htmlReport: HTMLReport;
  isContentLoading = true;
  analysisCode = signal<string>('');
  contentId: string;
  moduleName: string;
  reportName: string;
  subscriptionId: string;
  channelId = signal<string>('');
  lockedMessage = 'Full access to this section requires a subscription';
  showDownloadButton = signal(false);
  toolbarOptions: OptionGroup[];
  entitled = true;
  reportTitle: string;
  downLoadInProgress = false;
  addToFavoritesFF = toSignal(this.ffService.getFlag(ffKeys.enableAddToFavoritesHygraph));
  enableNotificationsFF = toSignal(
    this.ffService.getFlag(ffKeys.enablePlatformNotifications)
  );
  isTablet = toSignal(isTablet());
  fitWidth = false;

  stage = HYGRAPH_CONTENT_STAGES.PUBLISHED;
  selectedStage = HYGRAPH_CONTENT_STAGES.PUBLISHED;
  isFollowing = signal(false);

  private currentAssetDetails: AssetDetails;

  private readonly userProfile$ = this.store.select(selectUserProfile);
  private readonly showPublicBanner$ = new Subject<boolean>();
  private readonly requestAccessString$ = new BehaviorSubject<string>('');

  constructor(
    public prevPage: PreviousPageService,
    private readonly hygraphService: HygraphService,
    private readonly navigationService: NavigationService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly metaService: MetaService,
    private readonly dialogService: DialogService,
    private readonly store: Store,
    private readonly viewHistoryService: RecentViewHistoryService,
    private readonly myLibraryService: MyLibraryService,
    private readonly ffService: FeatureFlagService,
    private readonly authenticationService: AuthenticationService,
    private readonly notificationSettingsService: NotificationSettingsService,
  ) {
    effect(() => {
      this.toolbarOptions = this.createToolbarOptions();
    });

    effect(() => {
      this.updateRequestAccessString();
    });
  }

  ngOnInit(): void {
    this.toolbarOptions = this.createToolbarOptions();

    this.route.params
      .pipe(
        concatMap((routeParams) => {
          const fragment = this.route.snapshot.fragment;
          const params = new URLSearchParams(fragment);
          this.contentId = routeParams.contentId;
          this.moduleName = params.get('moduleName');
          this.analysisCode.set(params.get('reportCode'));
          this.subscriptionId = params.get('subscriptionId');
          this.channelId.set(params.get('channelId'));
          this.reportName = params.get('reportName');

          return this.initializeContent();
        }),
        untilDestroyed(this)
      ).subscribe(() => {
        this.currentAssetDetails = this.htmlReport.assetDetails.find(
          x => x.productCode === this.analysisCode()
        ) ?? this.htmlReport.assetDetails[0];
        this.postTelemetryEventForHygraph();
        this.sendViewHistoryEvent();
        this.loadNotificationStatus();
      });

  }

  public ngOnDestroy(): void {
    this.requestAccessString$.complete();
  }

  public showPublicBanner(): Observable<boolean> {
    return this.showPublicBanner$.asObservable();
  }

  public onClickBack() {
    this.prevPage.navigateBack();
  }

  public getRequestAccessString(): Observable<string> {
    return this.requestAccessString$.asObservable();
  }

  public download() {
    this.downLoadInProgress = true;
    combineLatest([
      this.userProfile$,
      this.fetchData(this.contentId, this.selectedStage)
    ]).pipe(untilDestroyed(this))
      .subscribe(([profile]) => {
        // Wait for rendering to finish so images are present
        setTimeout(() => {
          const iframe = this.invisibleIframe.nativeElement;
          const doc = iframe.contentWindow?.document ?? iframe.contentDocument;
          if (!doc) return;

          // Write content to the iframe
          doc.open();
          // creating a new html node
          const html = document.createElement('html');

          // We can load the CSS by cloning the document head
          const head = document.head.cloneNode(true);

          // creating a new body element for our newWindow
          const body = document.createElement('body');
          const data = this.hygraphElement.nativeElement.cloneNode(true);
          const waterMark = document.createElement('div');
          waterMark.innerText = `Downloaded by TechInsights subscriber ${profile.firstName} ${profile.lastName}, ${profile.email}`;

          data.appendChild(waterMark);
          body.appendChild(data);
          html.appendChild(head);
          html.appendChild(body);

          doc.write(html.outerHTML);
          doc.close();
          // Wait for content to load and then print
          doc.addEventListener('DOMContentLoaded', () => {
            // Select images within hygraphElement with specific alt attribute
            const images = doc.querySelectorAll('img[alt="Section Image"]');
            let imagesLoaded = 0;

            if (images.length === 0) {
              waitForIframeContent(iframe, () => {
                this.downLoadInProgress = false;
                iframe.contentWindow?.print();
              });
            } else {
              images.forEach((img: HTMLImageElement) => {
                const markLoaded = () => {
                  imagesLoaded++;
                  if (imagesLoaded === images.length) {
                    waitForIframeContent(iframe, () => {
                      this.downLoadInProgress = false;
                      iframe.contentWindow?.print();
                    });
                  }
                };
                // Checks if image was already loaded
                if (img.complete) {
                  markLoaded();
                } else {
                  img.addEventListener('load', markLoaded);
                }
              });
            }
          });

          iframe.contentWindow?.addEventListener('afterprint', () => {
            this.postTelemetryEventForHTMLDownload();
          });
        });
      }
      );
  }

  public handleStageChange(stage: string): void {
    const previousStage = this.selectedStage;

    if (stage === previousStage) {
      return;
    }

    this.isContentLoading = true;
    this.hygraphService.getReport(this.contentId, this.analysisCode(), stage).pipe(
      map((hygraphResp: HTMLReport) => {
        this.selectedStage = stage;
        this.setReportProps(hygraphResp);
      }),
      catchError((error) => {
        this.isContentLoading = false;

        this.selectedStage = '';
        setTimeout(() => {
          this.selectedStage = previousStage;
        }, 0);

        if (error.status === 404) {
          this.dialogService.openError({
            title: 'Content not available',
            closeButtonLabel: 'Close',
            body: 'Select another version.'
          });
        }
        return of(void 0);
      }),
      untilDestroyed(this)
    ).subscribe();
  }

  public share() {
    this.dialogService.open(
      ShareItemModalComponent,
      { windowClass: 'shareModalClass' },
      {
        isDocumentShareable: false,
      }
    );
  }

  public toggleNotification(): void {
    if (this.isFollowing()) {
      this.unfollow();
    } else {
      this.follow();
    }
  }

  public follow() {
    const previousState = this.isFollowing();

    // Optimistically update the UI
    this.isFollowing.set(true);

    const reportProgress = {
      reportId: this.currentAssetDetails?.reportId,
      reportName: this.currentAssetDetails?.name,
      reportCode: this.currentAssetDetails?.productCode,
      notifyFirstWipDeliverable: true,
      notifyAnalysisPublished: true,
      channel: this.channelId(),
      subscription: this.subscriptionId,
    };

    this.notificationSettingsService.saveReportProgressSubscription(reportProgress)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          // Already set to true optimistically, no need to set again
        },
        error: () => {
          // Revert to actual previous state on error
          this.isFollowing.set(previousState);
        }
      });
  }

  public unfollow() {
    const previousState = this.isFollowing();

    // Optimistically update the UI
    this.isFollowing.set(false);

    this.notificationSettingsService.deleteReportProgressSubscription(this.currentAssetDetails?.reportId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          // Already set to false optimistically, no need to set again
        },
        error: () => {
          // Revert to actual previous state on error
          this.isFollowing.set(previousState);
        }
      });
  }

  private initializeContent(): Observable<void> {
    // Always try to load Published first
    this.selectedStage = HYGRAPH_CONTENT_STAGES.PUBLISHED;
    return this.tryLoadPublishedWithPreviewFallback();
  }

  private tryLoadPublishedWithPreviewFallback(): Observable<void> {
    this.isContentLoading = true;
    return this.hygraphService.getReport(this.contentId, this.analysisCode(), HYGRAPH_CONTENT_STAGES.PUBLISHED).pipe(
      map((hygraphResp: HTMLReport) => {
        // Successfully loaded published content
        this.setReportProps(hygraphResp);
      }),
      catchError((error) => {
        if (error.status === 404) {
          // Published not available, check if user has preview access and try preview
          return this.authenticationService.me().pipe(
            concatMap((user) => {
              const hasPreviewAccess = isUserAllowedToPreviewHygraph(user);
              if (hasPreviewAccess) {
                // Try preview as fallback
                return this.hygraphService.getReport(
                  this.contentId,
                  this.analysisCode(),
                  HYGRAPH_CONTENT_STAGES.PREVIEW
                ).pipe(
                  map((previewResp: HTMLReport) => {
                    // Preview exists - load it and update dropdown to show preview
                    this.selectedStage = HYGRAPH_CONTENT_STAGES.PREVIEW;
                    this.setReportProps(previewResp);
                  }),
                  catchError(() => {
                    // Neither published nor preview available
                    this.isContentLoading = false;
                    this.dialogService.openError({
                      title: 'Content not available',
                      closeButtonLabel: 'Close',
                      body: 'No published or preview version available.'
                    });
                    return of(void 0);
                  })
                );
              } else {
                // User doesn't have preview access and published doesn't exist
                this.isContentLoading = false;
                this.dialogService.openError({
                  title: 'Content not available',
                  closeButtonLabel: 'Close',
                  body: 'No published version available.'
                });
                return of(void 0);
              }
            })
          );
        }
        this.isContentLoading = false;
        return of(void 0);
      }),
      untilDestroyed(this)
    );
  }

  private createToolbarOptions(): OptionGroup[] {
    const optionGroups: OptionGroup[] = [];
    const options: SocialOption[] = [];

    if (!this.isTablet()) {
      const tooltip = 'Toggle Width';
      options.push(
        new SocialOption('fit-width', { click: this.toggleFullWidth.bind(this) }, new ToggleBehavior(Icon.HYGRAPH_FIT_WIDTH, tooltip, Icon.HYGRAPH_DEFAULT_WIDTH, tooltip))
      )
    }

    // Add notification bell toggle
    if (this.enableNotificationsFF()) {
      const notificationTooltip = 'Follow to receive content status updates';
      options.push(
        new SocialOption('notification', { click: this.toggleNotification.bind(this) },
          new ToggleBehavior(
            Icon.IMAGE_NOTIFICATION,
            notificationTooltip,
            Icon.IMAGE_NOTIFICATION,
            notificationTooltip,
            this.isFollowing()
          )
        )
      );
    }

    if (this.showDownloadButton()) {
      options.push(
        new SocialOption('download', { click: this.download.bind(this) }, new StatelessBehavior(Icon.REPORT_VIEWER_DOWNLOAD, 'Download')),
      );
    }

    if (this.addToFavoritesFF()) {
      options.push(
        new SocialOption('favorite', { click: this.addParentAnalysisToFavorites.bind(this) }, new StatelessBehavior(Icon.IMAGE_FAVORITE, 'Add to Favorites'))
      );
    }


    options.push(
      new SocialOption('share', { click: this.share.bind(this) }, new StatelessBehavior(Icon.IMAGE_SHARE, 'Share'))
    );

    optionGroups.push(new OptionGroup(options));

    return optionGroups;
  }

  private toggleFullWidth() {
    this.fitWidth = !this.fitWidth;
  }

  private updateRequestAccessString(): void {
    const channelId = this.channelId();
    const analysisCode = this.analysisCode();

    if (!channelId || channelId === 'null') {
      this.requestAccessString$.next(analysisCode);
    } else {
      this.navigationService.getChannelById(channelId).subscribe(
        channel => this.requestAccessString$.next(`${channel.name}:${analysisCode}`)
      );
    }
  }

  private addParentAnalysisToFavorites() {
    this.myLibraryService.addAnalysisToFavoritesByAnalysisCode(this.analysisCode()).subscribe();
  }

  private loadNotificationStatus(): void {
    if (this.contentId) {
      this.notificationSettingsService.getReportProgressSubscriptions()
        .pipe(untilDestroyed(this))
        .subscribe({
          next: (subscriptions) => {
            const subscription = subscriptions.find(sub => sub.reportId === this.currentAssetDetails?.reportId);
            if (subscription && subscription.notifyFirstWipDeliverable && subscription.notifyAnalysisPublished) {
              this.isFollowing.set(true);
            } else {
              this.isFollowing.set(false);
            }
          },
          error: (error) => {
            // Failed to check notification status
          }
        });
    }
  }

  private setReportProps(report: HTMLReport) {
    this.htmlReport = report;
    this.entitled = report.entitled;
    if (report.entitled) {
      this.showPublicBanner$.next(false);
    } else {
      this.showPublicBanner$.next(true);
    }
    this.reportTitle = this.trimPrefix(this.htmlReport.header.title);
    this.showDownloadButton.set(report.downloadEnabled);
    this.isContentLoading = false;
  }

  private fetchData(contentId: string, stage?: string): Observable<void> {
    this.isContentLoading = true;
    return this.hygraphService.getReport(contentId, this.analysisCode(), stage).pipe(
      map((hygraphResp: HTMLReport) => {
        this.setReportProps(hygraphResp)
      }),
      catchError((error) => {
        this.isContentLoading = false;
        if (error.status === 404) {
          this.dialogService.openError({
            title: 'Content not available',
            closeButtonLabel: 'Close',
            body: 'Select another version.'
          });
        }
        return of(void 0);
      }),
      untilDestroyed(this)
    )
  }

  private trimPrefix(title: string) {
    return title?.replace(RegularExpressions.startsWithPinned, '');
  }
  private postTelemetryEventForHygraph() {
    const payload = {
      artifactName: `${this.htmlReport.name}.hg`,
      artifactType: 'application/hygraph',
      channelId: this.channelId(),
      subscriptionId: this.subscriptionId,
      reportCode: this.htmlReport.reportCode || this.analysisCode(),
      reportId: this.contentId,
      reportTitle: this.htmlReport.reportTitle || this.reportName,
      isSubscribed: this.entitled
    }
    const event = new TelemetryEvent(TelemetryEventName.EVENT_HG_ASSET_VIEW, payload, this.moduleName);
    this.metaService.postTelemetryEvent(event).pipe(untilDestroyed(this)).subscribe();
  }

  private postTelemetryEventForHTMLDownload() {
    const payload = {
      artifactName: `${this.htmlReport.name}.hg`,
      artifactType: 'application/hygraph',
      channelId: this.channelId(),
      subscriptionId: this.subscriptionId,
      reportCode: this.htmlReport.reportCode || this.analysisCode(),
      reportId: this.contentId,
      reportTitle: this.htmlReport.reportTitle || this.reportName,
      downloadSizeBytes: 0
    }
    const event = new TelemetryEvent(TelemetryEventName.EVENT_HG_ASSET_DOWNLOAD, payload, this.moduleName);
    this.metaService.postTelemetryEvent(event).pipe(untilDestroyed(this)).subscribe();
  }

  private sendViewHistoryEvent() {
    this.viewHistoryService.postRecentViewHistory({
      itemName: this.reportName,
      itemType: UserViewHistoryItemType.REPORT,
      itemSource: UserViewHistoryItemSource.META,
      itemPublicationDate: new Date(this.htmlReport.publishDate),
      channelSfId: this.channelId(),
      subscriptionSfId: this.subscriptionId,
      moduleId: this.moduleName,
      clObjectId: null,
      itemId: this.contentId,
      payload: {
        url: window.location.toString(),
        reportCode: this.analysisCode(),
        isAnnotated: false,
        container: 'hygraph-viewer',
      },
    }).pipe(untilDestroyed(this))
      .subscribe();
  }

}
